import { askBeforeLeaving, hasUnsaved, leaveNow, startUnsaved } from "../ui/unsaved";
import { dropMe, keepMe } from "./me-cache";
import { useZone } from "../ui/zone";
import { api, el, tipHide, toast } from "../ui/dom";
import { openDialog } from "../ui/modal";
import { icon } from "../ui/icons";
import { poweredBy } from "../ui/powered";
import { homeScreen } from "../screens/home";
import { emptyScreen, notAvailableScreen } from "../screens/empty";
import { hasOverview, overviewScreen } from "../screens/overview";
import { brandingScreen } from "../screens/settings/branding";
import { modulesScreen } from "../screens/settings/modules";
import { usersScreen } from "../screens/settings/users";
import { weekScreen } from "../screens/settings/week";
import { clientsScreen } from "../screens/clients/clients";
import { peopleScreen } from "../screens/people/people";
import { patientsScreen } from "../screens/patients/patients";
import { authorizationsScreen } from "../screens/patients/authorizations";
import { visitsScreen } from "../screens/patients/visits";
import { billRatesScreen } from "../screens/billing/bill-rates";
import { billingScreen } from "../screens/billing/billing";
import { paymentsScreen } from "../screens/billing/payments";
import { payrollScreen } from "../screens/billing/payroll";
import { businessesScreen } from "../screens/settings/businesses";
import { planBillScreen } from "../screens/settings/planbill";
import { servicesScreen } from "../screens/settings/services";
import { listsScreen } from "../screens/settings/lists";
import { clientTypesScreen } from "../screens/settings/clienttypes";
import { homeCareSettingsScreen } from "../screens/settings/homecare";
import { intakeScreen } from "../screens/settings/intake";
import { libraryScreen } from "../screens/settings/library";
import { integrationsScreen } from "../screens/settings/integrations";
import { workflowsScreen } from "../screens/settings/workflows";
import { aiScreen } from "../screens/settings/ai";
import { inboxScreen, type InboxCounts } from "../screens/inbox/inbox";
import { COUNTS_EVENT, live } from "./live";
import { applyBranding, applyTextSize, TEXT_LABEL } from "./brand";
import { openSearch } from "./cmdk";
import { startHeartbeat } from "./heartbeat";
import { restoreDrafts, startIdle } from "./idle";
import { setAiStates } from "../ui/textbox";
import { fieldApp } from "../phone/field";
import { openMyProfile } from "../screens/profile";
import { tenantsScreen } from "../screens/platform/tenants";
import { noteModule } from "../ui/setup-bar";
import type { Me, MenuSection, ScreenContext, TextSize } from "./types";

// The frame every screen sits in (brief 1A-5, docs/design/mockup.html): the top bar with the app
// name and logo, the top menu, Ctrl K, inbox, training and your menu; the left menu of the section
// you're in, which folds to icons; and the screen. At phone width the top menu and the left menu
// move into one menu sheet.

/** Screens with something real on them; every other menu item opens an empty screen. */
const BUILT: Record<string, (host: HTMLElement, ctx: ScreenContext) => void | Promise<void>> = {
  "settings/modules": modulesScreen,
  "settings/branding": brandingScreen,
  "settings/users": usersScreen,
  "settings/time": weekScreen,
  "staffing/clients": clientsScreen,
  "homecare/clients": clientsScreen,
  "transport/clients": clientsScreen,
  "homecare/caregivers": peopleScreen,
  "homecare/patients": patientsScreen,
  "homecare/authorizations": authorizationsScreen,
  "homecare/visits": visitsScreen,
  "homecare/rates": billRatesScreen,
  "homecare/billing": billingScreen,
  "homecare/payments": paymentsScreen,
  "homecare/payroll": payrollScreen,
  "settings/businesses": businessesScreen,
  "settings/planbill": planBillScreen,
  "settings/services": servicesScreen,
  "settings/lists": listsScreen,
  "homecare/settings": homeCareSettingsScreen,
  "transport/drivers": peopleScreen,
  "settings/library": libraryScreen,
  "settings/clienttypes": clientTypesScreen,
  "settings/intake": intakeScreen,
  "settings/integrations": integrationsScreen,
  "settings/workflows": workflowsScreen,
  "settings/ai": aiScreen,
  "platform/tenants": tenantsScreen,
  ...Object.fromEntries(
    [
      "overview",
      "scans",
      "work",
      "new",
      "docs",
      "flags",
      "fyi",
      "outbox",
      "done",
      "filtered",
      "sources",
    ].map((id) => [`inbox/${id}`, inboxScreen]),
  ),
};

/** Screens that open a record at /section/screen/<record id>. */
const HAS_RECORDS = new Set([
  // An integration opens at /settings/integrations/<id> (brief 1B-5: Calls).
  "settings/integrations",
  // A workflow opens at /settings/workflows/<key> (brief 1B-7).
  "settings/workflows",
  "staffing/clients",
  "homecare/clients",
  "transport/clients",
  "homecare/caregivers",
  "homecare/patients",
  // A bill rate card opens at /homecare/rates/<id> (brief 1E-16).
  "homecare/rates",
  // An invoice opens at /homecare/billing/<id> (brief 1E-1).
  "homecare/billing",
  // A posting opens at /homecare/payments/<id> (brief 1E-3).
  "homecare/payments",
  // Home Care › Settings opens Scheduling at /homecare/settings/scheduling (brief 1D-2).
  "homecare/settings",
  "transport/drivers",
  // An inbox item opens at its screen's address plus the item's id (brief 1B-3).
  ...["overview", "work", "new", "docs", "flags", "fyi", "done", "filtered"].map(
    (id) => `inbox/${id}`,
  ),
]);

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const isPhone = () => window.matchMedia("(max-width:760px)").matches;

export function startShell(first: Me, fresh?: Promise<Me | null>) {
  let me = first;
  useZone(me.timeZone);
  /** The team inbox is there: its screens reach this person and the company has it on (1B-5). */
  const hasInbox = () =>
    me.menus.some((m) => m.id === "inbox" && m.screens.some((x) => x.id === "overview"));
  const top = document.getElementById("top") as HTMLElement;
  const app = document.getElementById("view") as HTMLElement;
  document.body.classList.add("signed-in");
  const foldKey = () => `fc-rmin:${me.user.id}`;
  const folded = () => {
    try {
      return localStorage.getItem(foldKey()) === "1";
    } catch {
      return false;
    }
  };

  // ---- Where we are ----------------------------------------------------------------------------

  function where(): {
    section?: MenuSection;
    screenId?: string;
    record?: string;
    known: boolean;
  } {
    const [sectionId, screenId, ...rest] = location.pathname.split("/").filter(Boolean);
    if (!sectionId) return { known: true };
    const section = me.menus.find((m) => m.id === sectionId);
    if (!section) return { known: false };
    // One more part is a record, on screens that have records.
    const record = rest.length === 1 ? decodeURIComponent(rest[0] as string) : undefined;
    if (rest.length > 1 || (record && !HAS_RECORDS.has(`${sectionId}/${screenId}`)))
      return { known: false };
    return { section, screenId: screenId ?? "overview", record, known: true };
  }

  function go(path: string) {
    if (path !== location.pathname) history.pushState(null, "", path);
    render();
    window.scrollTo(0, 0);
  }
  // Back and forward (brief 1E-23): with unsaved typing it asks Save, Discard or Stay first.
  let shown = location.pathname + location.search;
  window.addEventListener("popstate", () => {
    const to = location.pathname + location.search;
    if (to === shown) return;
    if (!hasUnsaved()) {
      shown = to;
      void render();
      return;
    }
    history.pushState(null, "", shown);
    void askBeforeLeaving().then((ok) => {
      if (!ok) return;
      history.pushState(null, "", to);
      shown = to;
      void render();
    });
  });

  // ---- Top bar ---------------------------------------------------------------------------------

  function navButton(
    path: string,
    label: (Node | string)[],
    attrs: Record<string, string | undefined> = {},
  ) {
    const b = el("button", { type: "button", "data-href": path, ...attrs }, ...label);
    b.addEventListener("click", () => go(path));
    return b;
  }

  function drawTop() {
    const here = where();
    // The company's logo (or its initials in the brand color) and short name, the full name on
    // hover (brief 1D-3, mockup v40). The app name stays the browser tab's.
    const tb = me.topBar;
    const brand = el("button", {
      type: "button",
      class: "brand",
      "aria-label": `${tb.full} home`,
      "data-tip": tb.full,
      "data-href": "/",
    });
    brand.append(
      tb.logo
        ? el("img", { src: tb.logo, alt: `${tb.full} logo` })
        : el("span", { class: "brandmark", "aria-hidden": "true" }, tb.initials),
      ...(tb.name ? [el("span", { class: "brandname" }, tb.name)] : []),
    );
    brand.addEventListener("click", () => go("/"));

    const tops = me.menus.filter((m) => m.top);
    const sw = el("nav", { class: "switch", "aria-label": "Top menu" });
    let sep = false;
    for (const m of tops) {
      if ((m.id === "business" || m.id === "compliance") && !sep) {
        sep = true;
        sw.append(el("span", { class: "swsep" }));
      }
      sw.append(
        navButton(`/${m.id}/overview`, [icon(m.icon), ` ${m.name}`], {
          "aria-pressed": here.section?.id === m.id ? "true" : "false",
          "data-tip": m.tip,
        }),
      );
    }

    const menuBtn = el(
      "button",
      {
        type: "button",
        class: "btn ib phonly",
        "aria-label": "Menu",
        "data-tip": "Menu",
        "aria-haspopup": "dialog",
      },
      icon("three-bars"),
    );
    menuBtn.addEventListener("click", openSheet);

    const find = el(
      "button",
      {
        type: "button",
        class: "btn ib-wide",
        "aria-label": "Search",
        "data-tip": "Search screens (Ctrl K)",
        "aria-keyshortcuts": "Control+K Meta+K",
      },
      icon("search"),
      el("span", { class: "kbd" }, /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘ K" : "Ctrl K"),
    );
    find.addEventListener("click", () => openSearch(me, go));

    // The shared inbox (brief 1B-3), for people whose roles reach it; the badge is the open count.
    const inbox = hasInbox()
      ? el(
          "button",
          {
            type: "button",
            class: `btn ib inboxbtn${here.section?.id === "inbox" ? " on" : ""}`,
            "aria-label": "Inbox",
            "data-tip": "Shared inbox",
            "data-inboxbtn": "1",
          },
          icon("inbox"),
        )
      : null;
    inbox?.addEventListener("click", () => go("/inbox/overview"));
    const training = el(
      "button",
      {
        type: "button",
        class: "btn ib",
        "aria-label": "Training",
        "data-tip": "Training: short lessons for each role · coming soon",
      },
      icon("mortar-board"),
    );
    training.addEventListener("click", () => toast("Training is coming soon"));

    // Office / Field app (brief 1D-3): someone who is also a caregiver sees their own phone app.
    const mode = me.field
      ? el(
          "div",
          { class: "modeTog", role: "group", "aria-label": "View" },
          navButton("/", [icon("organization"), " Office"], {
            "aria-pressed": "true",
            "data-tip": "Office screens",
          }),
          navButton("/field/care", [icon("device-mobile"), " Field app"], {
            "aria-pressed": "false",
            "data-tip": "Phone app: visits, check-in, tasks, sign-off",
          }),
        )
      : null;
    top.replaceChildren(
      ...banners(),
      brand,
      ...(tops.length ? [sw] : []),
      el("div", { class: "topright" }, mode, menuBtn, find, inbox, training, yourMenu()),
    );
  }

  // ---- The tenant banner and the View as banner (brief 1E-6, mockup v59) ----------------------

  /** Viewing as a job: the job, read-only, the minutes left, and Stop viewing. */
  function viewAsBanner(): HTMLElement | null {
    const v = me.viewAs;
    if (!v) return null;
    const stopBtn = el(
      "button",
      { type: "button", class: "btn sm" },
      icon("sign-out"),
      " Stop viewing",
    );
    stopBtn.addEventListener("click", async () => {
      stopBtn.disabled = true;
      await api("POST", "/api/view-as/stop", {});
      location.assign("/settings/users");
    });
    return el(
      "div",
      { class: "tbanner vabanner", role: "status", "data-viewas": v.job },
      icon("eye"),
      " Viewing as ",
      el("b", {}, v.job),
      ` · read-only · ends in ${v.minutesLeft} min · recorded in the audit log `,
      stopBtn,
    );
  }

  /** A FlowCare staff member inside a company: which one, and Exit tenant. */
  function tenantBanner(): HTMLElement | null {
    const t = me.platform?.inside;
    if (!t) return null;
    const exitBtn = el(
      "button",
      { type: "button", class: "btn sm" },
      icon("sign-out"),
      " Exit company",
    );
    exitBtn.addEventListener("click", async () => {
      exitBtn.disabled = true;
      await api("POST", "/api/platform/exit", {});
      location.assign("/platform/tenants");
    });
    return el(
      "div",
      { class: "tbanner", role: "status", "data-tenant-banner": "1" },
      icon("eye"),
      " Viewing company ",
      el("b", {}, t.name),
      " · ",
      el("span", { class: `chip ${t.test ? "info" : "ok"}` }, t.test ? "Test company" : t.status),
      ` · ${t.modules.join(", ") || "No modules on"}${t.test ? " · sample records" : ""} `,
      exitBtn,
    );
  }

  const banners = () => [viewAsBanner(), tenantBanner()].filter((b): b is HTMLElement => !!b);

  // The view ends by itself at its time limit: the app reloads as the Admin's own.
  if (me.viewAs) {
    const left = new Date(me.viewAs.endsAt).getTime() - Date.now();
    const tick = window.setInterval(() => {
      const banner = document.querySelector(".vabanner");
      const v = me.viewAs;
      if (!banner || !v) return;
      const mins = Math.max(1, Math.ceil((new Date(v.endsAt).getTime() - Date.now()) / 60_000));
      for (const n of banner.childNodes)
        if (n.nodeType === Node.TEXT_NODE && n.textContent?.includes("ends in"))
          n.textContent = ` · read-only · ends in ${mins} min · recorded in the audit log `;
    }, 30_000);
    window.setTimeout(
      () => {
        window.clearInterval(tick);
        toast(`Viewing as ${me.viewAs?.job ?? "a job"} ended`, "Access is back to normal");
        location.assign("/settings/users");
      },
      Math.max(0, left) + 1_000,
    );
  }

  /** Asks for the inbox's open counts: the top bar's badge and, in the inbox, the left menu's. */
  let countsAsked = 0;
  async function inboxCounts() {
    if (!hasInbox()) return;
    const n = ++countsAsked;
    const res = await api<InboxCounts>("GET", "/api/inbox/counts", undefined, {
      background: true,
    });
    if (n !== countsAsked || !res.ok) return;
    drawCounts(res.body);
  }
  function drawCounts(c: InboxCounts) {
    const btn = document.querySelector<HTMLElement>("[data-inboxbtn]");
    if (btn) {
      btn.querySelector(".ibadge")?.remove();
      if (c.open) btn.append(el("span", { class: "ibadge" }, String(c.open)));
      btn.setAttribute("aria-label", `Inbox, ${c.open} open`);
      btn.dataset.tip = `Shared inbox · ${c.open} open from every business`;
    }
    for (const span of document.querySelectorAll<HTMLElement>(".rail [data-cnt]")) {
      const v = c[span.dataset.cnt as keyof InboxCounts];
      span.textContent = v ? String(v) : "";
      span.hidden = !v;
    }
  }
  window.addEventListener("fc:inbox-changed", () => void inboxCounts());
  // The live inbox (brief 1B-6): an inbox screen's own answer carries the counts; on every other
  // screen the shell asks for them every 15 seconds while the tab is visible.
  window.addEventListener(COUNTS_EVENT, (e) => {
    countsAsked++;
    drawCounts((e as CustomEvent<InboxCounts>).detail);
  });
  live.watch("counts", async () => {
    if (!live.has("inbox")) await inboxCounts();
  });

  // ---- Your menu: name, company, text size, settings you can open, sign out ---------------------

  function yourMenu(): HTMLElement {
    const wrap = el("div", { class: "amwrap" });
    const btn = el(
      "button",
      {
        type: "button",
        class: "avatar-btn",
        "aria-haspopup": "menu",
        "aria-expanded": "false",
        "aria-label": "Account menu",
        "data-tip": "Account: text size, settings, sign out",
      },
      initials(me.user.name),
    );
    wrap.append(btn);
    let menu: HTMLElement | null = null;

    const close = () => {
      menu?.remove();
      menu = null;
      btn.setAttribute("aria-expanded", "false");
      document.removeEventListener("mousedown", outside, true);
      document.removeEventListener("keydown", onKey, true);
    };
    const outside = (e: Event) => {
      if (!wrap.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        close();
        btn.focus();
      }
    };

    btn.addEventListener("click", () => {
      if (menu) return close();
      tipHide();
      const own = me.user.textSize ?? me.textSize;
      const seg = el(
        "div",
        { class: "seg", role: "group", "aria-label": "Text size" },
        ...(["small", "standard", "large"] as TextSize[]).map((size) => {
          const b = el(
            "button",
            { type: "button", "aria-pressed": own === size ? "true" : "false", "data-size": size },
            TEXT_LABEL[size],
          );
          b.addEventListener("click", () => setTextSize(size, seg));
          return b;
        }),
      );
      const settings = me.menus.find((m) => m.id === "settings");
      const links = settings
        ? (["overview", "modules", "users", "branding"] as const).flatMap((id) => {
            const s = settings.screens.find((x) => x.id === id);
            if (!s) return [];
            const b = el(
              "button",
              { type: "button", role: "menuitem" },
              icon(s.icon),
              ` ${s.title}`,
            );
            b.addEventListener("click", () => {
              close();
              go(`/settings/${id}`);
            });
            return [b];
          })
        : [];
      // FlowCare Platform (brief 1E-6, mockup v59): FlowCare staff only.
      const platform = me.menus.some((m) => m.id === "platform")
        ? el(
            "button",
            { type: "button", role: "menuitem", "data-platform": "1" },
            icon("apps"),
            " FlowCare Platform (staff)",
          )
        : null;
      platform?.addEventListener("click", () => {
        close();
        go("/platform/tenants");
      });
      // My profile (brief 1E-5): anyone signed in, the owner included.
      const profile = el(
        "button",
        { type: "button", role: "menuitem", "data-myprof": "1" },
        icon("person"),
        " My profile",
      );
      profile.addEventListener("click", () => {
        close();
        void openMyProfile((name) => {
          me.user.name = name;
        });
      });
      const out = el("button", { type: "button", role: "menuitem" }, icon("sign-out"), " Sign out");
      out.addEventListener("click", async () => {
        out.disabled = true;
        await fetch("/api/auth/signout", { method: "POST" }).catch(() => undefined);
        dropMe();
        leaveNow();
        location.assign("/");
      });
      menu = el(
        "div",
        { class: "amenu", role: "menu", "aria-label": "Account menu" },
        el(
          "div",
          { class: "amhead" },
          el("span", { class: "avatar" }, initials(me.user.name)),
          el("span", {}, el("b", {}, me.user.name), el("small", {}, me.company.name)),
        ),
        profile,
        el(
          "div",
          { class: "fsrow" },
          el("div", { class: "amlab" }, "Text size (this account only)"),
          seg,
        ),
        ...(links.length ? [el("div", { class: "amlab" }, "Company"), ...links] : []),
        ...(platform ? [el("hr"), platform] : []),
        el("hr"),
        out,
      );
      wrap.append(menu);
      btn.setAttribute("aria-expanded", "true");
      document.addEventListener("mousedown", outside, true);
      document.addEventListener("keydown", onKey, true);
    });
    return wrap;
  }

  async function setTextSize(size: TextSize, seg: HTMLElement) {
    applyTextSize(size);
    for (const b of seg.querySelectorAll<HTMLButtonElement>("button"))
      b.setAttribute("aria-pressed", b.dataset.size === size ? "true" : "false");
    const res = await api<{ own: TextSize | null; textSize: TextSize }>(
      "PUT",
      "/api/me/text-size",
      {
        textSize: size,
      },
    );
    if (!res.ok) {
      applyTextSize(me.textSize);
      return toast("Text size wasn't saved", res.body.error);
    }
    me = { ...me, user: { ...me.user, textSize: res.body.own }, textSize: res.body.textSize };
    toast(`Text size: ${TEXT_LABEL[size]}`, "Only this account sees this size");
  }

  // ---- Left menu -------------------------------------------------------------------------------

  function rail(section: MenuSection, screenId: string): HTMLElement {
    const min = folded();
    const nav = el("nav", { class: "rail", "aria-label": `${section.name} menu` });
    const tog = el(
      "button",
      {
        type: "button",
        class: "railtog",
        "aria-label": min ? "Show menu" : "Hide menu",
        "data-tip": min ? "Show menu" : "Hide menu",
        "aria-expanded": min ? "false" : "true",
      },
      icon(min ? "sb-open" : "sb-fold"),
    );
    tog.addEventListener("click", () => {
      try {
        localStorage.setItem(foldKey(), min ? "0" : "1");
      } catch {
        // Private windows may refuse storage; the menu still folds for this page.
      }
      tipHide();
      render();
      (document.querySelector(".railtog") as HTMLElement | null)?.focus();
    });
    nav.append(tog, el("div", { class: "eyebrow" }, section.brand));
    for (const s of section.screens) {
      if (s.id === "settings") nav.append(el("hr"));
      nav.append(
        navButton(
          `/${section.id}/${s.id}`,
          [
            el("span", { class: "rl" }, icon(s.icon), el("span", {}, s.title)),
            // The inbox's left menu shows how many are open on each screen (filled by inboxCounts).
            section.id === "inbox" &&
            !["scans", "outbox", "done", "filtered", "sources"].includes(s.id)
              ? el("span", { class: "cnt", "data-cnt": s.id, hidden: true })
              : "",
          ],
          {
            "aria-current": s.id === screenId ? "page" : "false",
            // Folded, only the icon shows: the name stays for screen readers and the hover tip.
            "aria-label": s.title,
            "data-tip": min ? s.title : undefined,
          },
        ),
      );
    }
    // FlowCare stays small, at the bottom of the menu (brief 1D-3, mockup v40).
    nav.append(poweredBy());
    return nav;
  }

  // ---- Phone: one menu sheet with the top menu and this section's left menu ----------------------

  function openSheet() {
    const here = where();
    const item = (path: string, ic: string, label: string, current: boolean) => {
      const b = el(
        "button",
        { type: "button", "aria-current": current ? "page" : "false" },
        el("span", { class: "rl" }, icon(ic), el("span", {}, label)),
      );
      b.addEventListener("click", () => {
        close();
        go(path);
      });
      return b;
    };
    const closeBtn = el(
      "button",
      { type: "button", class: "btn ib", "aria-label": "Close", "data-tip": "Close" },
      icon("x"),
    );
    const body = el(
      "div",
      { class: "msheet" },
      el("div", { class: "cardhead" }, el("h2", {}, "Menu"), closeBtn),
      el(
        "nav",
        { "aria-label": "Top menu" },
        item("/", "home", "Home", !here.section),
        ...me.menus
          .filter((m) => m.top)
          .map((m) => item(`/${m.id}/overview`, m.icon, m.name, here.section?.id === m.id)),
      ),
      ...(here.section
        ? [
            el(
              "nav",
              { "aria-label": `${here.section.name} menu` },
              el("div", { class: "eyebrow" }, here.section.brand),
              ...here.section.screens.map((s) =>
                item(`/${here.section?.id}/${s.id}`, s.icon, s.title, s.id === here.screenId),
              ),
            ),
          ]
        : []),
      poweredBy(),
    );
    const close = openDialog(body, { label: "Menu", className: "sheet" });
    closeBtn.addEventListener("click", () => close());
    (body.querySelector('[aria-current="page"]') as HTMLElement | null)?.focus();
  }

  // ---- The screen --------------------------------------------------------------------------------

  let drawn = 0;
  async function render() {
    const n = ++drawn;
    shown = location.pathname + location.search;
    tipHide();
    // The field app (brief 1D-3): a caregiver sees nothing else; anyone else who is also a
    // caregiver opens it from the top bar's Field app.
    const onField = location.pathname === "/field" || location.pathname.startsWith("/field/");
    if (me.field && (me.field.only || onField)) {
      if (!onField) history.replaceState(null, "", "/field/care");
      live.unwatch("inbox");
      document.getElementById("layer")?.replaceChildren();
      applyBranding(me.branding, me.textSize);
      top.hidden = true;
      document.body.classList.add("in-field");
      document.title = `Field app · ${me.branding.appName}`;
      await fieldApp(app, { me, go, office: !me.field.only });
      // Viewing as a field job, or inside a company: the banner stays over the field app.
      const bars = banners();
      if (bars.length) app.prepend(el("div", { class: "vabar" }, ...bars));
      return;
    }
    top.hidden = false;
    document.body.classList.remove("in-field");
    // A Branding preview that wasn't saved goes away when you leave the screen.
    applyBranding(me.branding, me.textSize);
    document.getElementById("layer")?.replaceChildren();
    // The screen going away stops asking for its changes (brief 1B-6); the next one starts its own.
    live.unwatch("inbox");
    drawTop();
    void inboxCounts();
    const here = where();
    const wrap = el("div", { class: "wrap" });
    const main = el("main", { id: "main" }, wrap);
    if (!here.section) {
      app.replaceChildren(main);
      if (here.known) {
        document.title = me.branding.appName;
        homeScreen(wrap, me, go);
      } else {
        document.title = `Not available · ${me.branding.appName}`;
        notAvailableScreen(wrap, go);
      }
      return;
    }
    const section = here.section;
    // Setup lists start on the module you came from (brief 1E-7).
    noteModule(section.id);
    const screen = section.screens.find((s) => s.id === here.screenId);
    const shell = el("div", { class: `shell${folded() && !isPhone() ? " rmin" : ""}` });
    shell.append(rail(section, here.screenId ?? ""), main);
    app.replaceChildren(shell);
    if (!screen) {
      document.title = `Not available · ${me.branding.appName}`;
      return notAvailableScreen(wrap, go);
    }
    // The server has the last word on whether this address may be opened. It is asked alongside
    // the screen's own data (brief 1E-29): the screen draws at once, with a quiet line until its
    // first view is there, and gives way to Not available if the answer says so.
    const check = api("GET", `/api/screens/${section.id}/${screen.id}`);
    document.title = `${screen.title} · ${me.branding.appName}`;
    const nav = crumbs(section, screen.id, screen.title);
    const waiting = el("p", { class: "muted", "aria-busy": "true" }, "Loading…");
    wrap.append(nav, waiting);
    const refused = async () => {
      const res = await check;
      if (n !== drawn || res.ok) return false;
      document.title = `Not available · ${me.branding.appName}`;
      wrap.replaceChildren();
      notAvailableScreen(wrap, go);
      return true;
    };
    const ctx: ScreenContext = {
      me,
      section,
      screen,
      record: here.record,
      crumb: (name) => {
        nav.querySelector(".crumbs .here")?.remove();
        nav
          .querySelector(".crumbs")
          ?.append(el("span", { class: "here", "aria-current": "page" }, "› ", name));
        document.title = `${name} · ${screen.title} · ${me.branding.appName}`;
      },
      go,
      refresh,
      applyBranding: (b, size) => applyBranding(b, size ?? me.textSize),
    };
    const built =
      BUILT[`${section.id}/${screen.id}`] ??
      (screen.id === "overview" && hasOverview(section.id) ? overviewScreen : undefined);
    const drawing = (async () => {
      if (built) await built(wrap, ctx);
      else emptyScreen(wrap, ctx);
    })();
    const out = refused();
    try {
      await drawing;
    } finally {
      waiting.remove();
    }
    await out;
  }

  function crumbs(section: MenuSection, screenId: string, title: string): HTMLElement {
    const c = el("div", { class: "crumbs" });
    const home = navButton("/", [icon("home"), " Home"]);
    // FlowCare Platform has no Overview of its own: its name opens Tenants.
    const sectionHome = section.id === "platform" ? "tenants" : "overview";
    c.append(home, "›", navButton(`/${section.id}/${sectionHome}`, [section.name]));
    if (screenId !== "overview" || section.id === "settings")
      c.append("›", navButton(`/${section.id}/${screenId}`, [title]));
    return el("nav", { "aria-label": "Breadcrumbs" }, c);
  }

  async function refresh() {
    const res = await api<Me>("GET", "/api/me");
    if (res.status === 401) {
      dropMe();
      return location.assign("/");
    }
    if (!res.ok) return;
    await apply(res.body);
  }

  /** A new /api/me: remembered for the visit, and the top bar and left menu drawn from it. */
  async function apply(next: Me) {
    keepMe(next);
    me = next;
    useZone(me.timeZone);
    applyBranding(me.branding, me.textSize);
    setAiStates(me.ai);
    drawTop();
    void inboxCounts();
    // Redraw the left menu only; the screen keeps what's on it.
    const here = where();
    const old = document.querySelector(".rail");
    if (old && here.section) old.replaceWith(rail(here.section, here.screenId ?? ""));
    else await render();
  }

  // ---- Keys --------------------------------------------------------------------------------------

  document.addEventListener("keydown", (e) => {
    if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === "k") {
      e.preventDefault();
      openSearch(me, go);
    }
  });

  applyBranding(me.branding, me.textSize);
  // Leaving a form with unsaved typing asks first (brief 1E-23).
  startUnsaved();
  // The mic and AI menu on every text box, while the company has them on (brief 1B-8).
  setAiStates(me.ai);
  startHeartbeat();
  startIdle(() => me);
  void render().then(restoreDrafts);
  // Drawn from what this tab remembered (brief 1E-29): the fresh answer replaces it, and the menus
  // are drawn again only when something changed. Someone else signed in: the page starts over.
  void fresh?.then(async (next) => {
    if (!next) return;
    if (next.user.id !== me.user.id) {
      keepMe(next);
      return location.reload();
    }
    if (JSON.stringify(next) === JSON.stringify(me)) return keepMe(next);
    await apply(next);
  });
}
